import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Zap,
  Clock,
  DollarSign,
  Package,
  Target,
  BarChart3,
  Plus,
  Trash2,
  RefreshCw,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [flashSales, setFlashSales] = useState([]);
  const [slowMovingItems, setSlowMovingItems] = useState([]);
  const [showFlashSaleDialog, setShowFlashSaleDialog] = useState(false);
  const [showSlowMovingDialog, setShowSlowMovingDialog] = useState(false);
  const [showMarkupPreviewDialog, setShowMarkupPreviewDialog] = useState(false);
  const [showMarkupResultsDialog, setShowMarkupResultsDialog] = useState(false);
  const [markupPreview, setMarkupPreview] = useState(null);
  const [markupResults, setMarkupResults] = useState(null);
  const [applyingMarkup, setApplyingMarkup] = useState(false);
  const [selectedSlowMovingItems, setSelectedSlowMovingItems] = useState([]);
  const [applyingDiscounts, setApplyingDiscounts] = useState(false);
  const [showComparePriceDialog, setShowComparePriceDialog] = useState(false);
  const [comparePriceMarkup, setComparePriceMarkup] = useState(50);
  const [comparePricePreview, setComparePricePreview] = useState(null);
  const [applyingComparePrice, setApplyingComparePrice] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [collections, setCollections] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState(null);
  const [showShopifyProductsDialog, setShowShopifyProductsDialog] = useState(false);
  const [selectedComparePriceItems, setSelectedComparePriceItems] = useState([]);
  const [selectAllComparePrice, setSelectAllComparePrice] = useState(false);
  
  // Flash Sale Form State
  const [flashSaleForm, setFlashSaleForm] = useState({
    name: "",
    description: "",
    discount_percentage: 20,
    target_type: "collection",
    target_value: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    fetchPerformance();
    fetchFlashSales();
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/pricing/collections`);
      if (response.data.success) {
        setCollections(response.data.collections);
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  const fetchShopifyProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pricing/shopify-products?limit=100`);
      if (response.data.success) {
        setShopifyProducts(response.data);
        setShowShopifyProductsDialog(true);
      }
    } catch (error) {
      console.error("Error fetching Shopify products:", error);
      toast.error("Failed to fetch Shopify products");
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(`${API}/pricing/performance`);
      if (response.data.success) {
        setPerformance(response.data.metrics);
      }
    } catch (error) {
      console.error("Error fetching performance:", error);
      toast.error("Failed to fetch pricing performance");
    }
  };

  const fetchFlashSales = async () => {
    try {
      const response = await axios.get(`${API}/pricing/flash-sales`);
      if (response.data.success) {
        setFlashSales(response.data.flash_sales);
      }
    } catch (error) {
      console.error("Error fetching flash sales:", error);
    }
  };

  const fetchSlowMovingItems = async (daysThreshold = 30) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pricing/slow-moving-items?days_threshold=${daysThreshold}`);
      if (response.data.success) {
        setSlowMovingItems(response.data.slow_moving_items);
        setSelectedSlowMovingItems([]);
        setShowSlowMovingDialog(true);
      }
    } catch (error) {
      console.error("Error fetching slow-moving items:", error);
      toast.error("Failed to fetch slow-moving items");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSlowMovingItem = (sku, discount) => {
    setSelectedSlowMovingItems((prev) => {
      const existing = prev.find((item) => item.sku === sku);
      if (existing) {
        return prev.filter((item) => item.sku !== sku);
      } else {
        return [...prev, { sku, discount_percentage: discount }];
      }
    });
  };

  const handleSelectAllSlowMoving = (checked) => {
    if (checked) {
      const allItems = slowMovingItems.slice(0, 50).map((item) => ({
        sku: item.sku,
        discount_percentage: item.suggested_discount,
      }));
      setSelectedSlowMovingItems(allItems);
    } else {
      setSelectedSlowMovingItems([]);
    }
  };

  const applyDiscountsToSlowMoving = async () => {
    if (selectedSlowMovingItems.length === 0) {
      toast.error("Please select items to apply discounts");
      return;
    }

    if (!window.confirm(`Apply suggested discounts to ${selectedSlowMovingItems.length} selected items?`)) {
      return;
    }

    setApplyingDiscounts(true);
    try {
      const response = await axios.post(`${API}/pricing/apply-discounts`, selectedSlowMovingItems);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowSlowMovingDialog(false);
        setSelectedSlowMovingItems([]);
        fetchPerformance();
      }
    } catch (error) {
      console.error("Error applying discounts:", error);
      toast.error("Failed to apply discounts");
    } finally {
      setApplyingDiscounts(false);
    }
  };

  const previewComparePrice = async () => {
    setLoading(true);
    try {
      const collection = selectedCollection === "all" ? null : selectedCollection;
      const collectionParam = collection ? `&collection=${encodeURIComponent(collection)}` : '';
      const response = await axios.get(`${API}/pricing/preview-compare-price?markup_percentage=${comparePriceMarkup}${collectionParam}&limit=50`);
      if (response.data.success) {
        setComparePricePreview(response.data);
        setShowComparePriceDialog(true);
      }
    } catch (error) {
      console.error("Error previewing compare price:", error);
      toast.error("Failed to preview compare price changes");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllComparePrice = (checked) => {
    setSelectAllComparePrice(checked);
    if (checked && comparePricePreview) {
      // Select all preview items (all items, not just first 20)
      const allSkus = comparePricePreview.preview_items.map(item => item.sku);
      setSelectedComparePriceItems(allSkus);
    } else {
      setSelectedComparePriceItems([]);
    }
  };

  const handleToggleComparePriceItem = (sku) => {
    setSelectedComparePriceItems(prev => {
      if (prev.includes(sku)) {
        return prev.filter(s => s !== sku);
      } else {
        return [...prev, sku];
      }
    });
  };

  const applyComparePrice = async () => {
    try {
      setApplyingComparePrice(true);
      
      // If items are selected, only apply to selected. Otherwise apply to all
      const skusToApply = selectedComparePriceItems.length > 0 
        ? selectedComparePriceItems 
        : null; // null means apply to all in collection
      
      const response = await axios.post(`${API}/pricing/compare-price/apply`, {
        markup_percentage: comparePriceMarkup,
        collection: selectedCollection,
        skus: skusToApply
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowComparePriceDialog(false);
        setComparePricePreview(null);
        setSelectedComparePriceItems([]);
        setSelectAllComparePrice(false);
        await fetchPerformance();
      }
    } catch (error) {
      console.error("Error applying compare price:", error);
      toast.error("Failed to apply compare at price");
    } finally {
      setApplyingComparePrice(false);
    }
  };

  const syncPricesFromShopify = async () => {
    if (!window.confirm("Sync all inventory prices from Shopify? This will update prices for all matched items.")) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/pricing/sync-from-shopify`);
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Show results
        const stats = response.data.stats;
        const message = `
Shopify Price Sync Complete!

✅ ${stats.updated} items updated
📦 ${stats.total_inventory} total inventory items
🔍 ${stats.matched_in_shopify} matched in Shopify
❌ ${stats.not_found_in_shopify} not found in Shopify

Sample updates:
${response.data.sample_updates.slice(0, 3).map(u => 
  `• ${u.sku}: Rs.${u.sale_price} (Profit: Rs.${u.profit})`
).join('\n')}
        `.trim();
        
        alert(message);
        fetchPerformance();
      }
    } catch (error) {
      console.error("Error syncing from Shopify:", error);
      toast.error("Failed to sync prices from Shopify");
    } finally {
      setLoading(false);
    }
  };

  const previewMarkup = async (markupPercentage) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pricing/preview-markup?markup_percentage=${markupPercentage}&limit=50`);
      if (response.data.success) {
        setMarkupPreview(response.data);
        setShowMarkupPreviewDialog(true);
      }
    } catch (error) {
      console.error("Error previewing markup:", error);
      toast.error("Failed to preview markup changes");
    } finally {
      setLoading(false);
    }
  };

  const applyMarkup = async (markupPercentage, collection = null) => {
    setApplyingMarkup(true);
    try {
      const params = new URLSearchParams({ markup_percentage: markupPercentage });
      if (collection) {
        params.append("collection", collection);
      }
      
      const response = await axios.post(`${API}/pricing/apply-markup?${params.toString()}`);
      if (response.data.success) {
        // Close preview dialog
        setShowMarkupPreviewDialog(false);
        
        // Fetch updated preview to show results
        const previewResponse = await axios.get(`${API}/pricing/preview-markup?markup_percentage=${markupPercentage}&limit=20`);
        if (previewResponse.data.success) {
          setMarkupResults({
            ...previewResponse.data,
            updated_count: response.data.updated_count,
            message: response.data.message
          });
          setShowMarkupResultsDialog(true);
        }
        
        toast.success(response.data.message);
        fetchPerformance();
      }
    } catch (error) {
      console.error("Error applying markup:", error);
      toast.error("Failed to apply markup");
    } finally {
      setApplyingMarkup(false);
    }
  };

  const createFlashSale = async () => {
    if (!flashSaleForm.name || !flashSaleForm.target_value || !flashSaleForm.start_date || !flashSaleForm.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/pricing/flash-sales`, {
        ...flashSaleForm,
        start_date: new Date(flashSaleForm.start_date).toISOString(),
        end_date: new Date(flashSaleForm.end_date).toISOString(),
      });
      
      if (response.data.success) {
        toast.success("Flash sale created successfully!");
        setShowFlashSaleDialog(false);
        setFlashSaleForm({
          name: "",
          description: "",
          discount_percentage: 20,
          target_type: "collection",
          target_value: "",
          start_date: "",
          end_date: "",
        });
        fetchFlashSales();
      }
    } catch (error) {
      console.error("Error creating flash sale:", error);
      toast.error("Failed to create flash sale");
    }
  };

  const toggleFlashSale = async (saleId, active) => {
    try {
      const response = await axios.put(`${API}/pricing/flash-sales/${saleId}?active=${!active}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchFlashSales();
      }
    } catch (error) {
      console.error("Error toggling flash sale:", error);
      toast.error("Failed to update flash sale");
    }
  };

  const deleteFlashSale = async (saleId) => {
    if (!window.confirm("Are you sure you want to delete this flash sale?")) return;
    
    try {
      const response = await axios.delete(`${API}/pricing/flash-sales/${saleId}`);
      if (response.data.success) {
        toast.success("Flash sale deleted");
        fetchFlashSales();
      }
    } catch (error) {
      console.error("Error deleting flash sale:", error);
      toast.error("Failed to delete flash sale");
    }
  };

  const getRemainingTime = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return "Expired";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Pricing & Offers Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Intelligent pricing strategies to maximize sales & liquidate inventory</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => fetchPerformance()}
              className="border-gray-300 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="default"
              onClick={() => setShowFlashSaleDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Zap className="w-4 h-4 mr-2" />
              Create Flash Sale
            </Button>
          </div>
        </div>

        {/* Performance Stats */}
        {performance && (
          <div className="grid grid-cols-6 gap-4 mt-6">
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Pricing Coverage</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{performance.pricing_coverage}%</p>
                  </div>
                  <Target className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Items Priced</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{performance.items_with_pricing}</p>
                  </div>
                  <Package className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Need Pricing</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">{performance.items_without_pricing}</p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Total Cost</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      Rs. {(performance.total_cost / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-gray-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Sale Value</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      Rs. {(performance.total_sale_value / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Profit</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      Rs. {(performance.total_profit / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-8">
        <div className="grid grid-cols-2 gap-6">
          {/* Quick Actions */}
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Pricing Actions</CardTitle>
              <CardDescription>Apply markup and pricing strategies instantly</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Sync Prices from Shopify
                </label>
                <Button
                  onClick={syncPricesFromShopify}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? "Syncing..." : "Sync All Prices from Shopify"}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  🔄 Import actual sale prices from your Shopify store
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Apply 30% Markup to All Unpriced Items
                </label>
                <Button
                  onClick={() => previewMarkup(30)}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loading ? "Loading Preview..." : "Preview 30% Markup"}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Formula: New Price = Cost × 1.30
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Set Compare At Price (Show Discount)
                </label>
                
                {/* Collection Selector */}
                <div className="mb-3">
                  <label className="text-xs font-medium text-gray-600 block mb-1">
                    Apply to Collection
                  </label>
                  <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select collection" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      {collections.map((col) => (
                        <SelectItem key={col.name} value={col.name}>
                          {col.name} ({col.items_with_price} priced items)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={comparePriceMarkup}
                    onChange={(e) => setComparePriceMarkup(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-semibold text-purple-600 min-w-[60px]">
                    +{comparePriceMarkup}%
                  </span>
                </div>
                <Button
                  onClick={previewComparePrice}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 mb-2"
                >
                  {loading ? "Loading..." : "Apply Compare Price"}
                </Button>
                
                {/* View Shopify Products Button */}
                <Button
                  onClick={fetchShopifyProducts}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Package className="w-4 h-4 mr-2" />
                  View All Shopify Products
                </Button>
                
                <p className="text-xs text-gray-500 mt-2">
                  💰 Show customers a discount (e.g., ~~Rs. 1,500~~ Rs. 1,000)
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Identify Slow-Moving Items (30+ days)
                </label>
                <Button
                  onClick={() => fetchSlowMovingItems(30)}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  {loading ? "Loading..." : "Find Slow-Moving Items"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Active Flash Sales Summary */}
          <Card className="border-gray-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Active Flash Sales</CardTitle>
                  <CardDescription>{flashSales.filter(s => s.active).length} active sales running</CardDescription>
                </div>
                <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                  <Zap className="w-3 h-3 mr-1" />
                  {flashSales.filter(s => s.active).length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {flashSales.filter(s => s.active).length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No active flash sales. Create one to boost sales!
                </p>
              ) : (
                <div className="space-y-3">
                  {flashSales.filter(s => s.active).slice(0, 3).map((sale) => (
                    <div key={sale.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{sale.name}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {sale.discount_percentage}% off • {sale.target_type}: {sale.target_value}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {getRemainingTime(sale.end_date)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Flash Sales Table */}
        <Card className="border-gray-200 mt-6">
          <CardHeader>
            <CardTitle className="text-lg">All Flash Sales</CardTitle>
            <CardDescription>Manage your time-limited offers and promotions</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Name</TableHead>
                  <TableHead className="font-semibold text-gray-700">Target</TableHead>
                  <TableHead className="font-semibold text-gray-700">Discount</TableHead>
                  <TableHead className="font-semibold text-gray-700">Start Date</TableHead>
                  <TableHead className="font-semibold text-gray-700">End Date</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flashSales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No flash sales created yet. Click "Create Flash Sale" to get started!
                    </TableCell>
                  </TableRow>
                ) : (
                  flashSales.map((sale) => (
                    <TableRow key={sale.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-gray-900">{sale.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {sale.target_type}: {sale.target_value}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-green-600">{sale.discount_percentage}%</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(sale.start_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(sale.end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {sale.active ? (
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleFlashSale(sale.id, sale.active)}
                            className={sale.active ? "border-orange-300 text-orange-700" : "border-green-300 text-green-700"}
                          >
                            {sale.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteFlashSale(sale.id)}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create Flash Sale Dialog */}
      <Dialog open={showFlashSaleDialog} onOpenChange={setShowFlashSaleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Flash Sale</DialogTitle>
            <DialogDescription>
              Set up a time-limited offer to boost sales and move inventory faster
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Sale Name *</label>
              <Input
                value={flashSaleForm.name}
                onChange={(e) => setFlashSaleForm({ ...flashSaleForm, name: e.target.value })}
                placeholder="e.g., Weekend Sneaker Sale"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Textarea
                value={flashSaleForm.description}
                onChange={(e) => setFlashSaleForm({ ...flashSaleForm, description: e.target.value })}
                placeholder="Optional sale description"
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Target Type *</label>
                <Select
                  value={flashSaleForm.target_type}
                  onValueChange={(v) => setFlashSaleForm({ ...flashSaleForm, target_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="collection">Collection</SelectItem>
                    <SelectItem value="sku">Specific SKU</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {flashSaleForm.target_type === "collection" ? "Collection Name" : "SKU"} *
                </label>
                <Input
                  value={flashSaleForm.target_value}
                  onChange={(e) => setFlashSaleForm({ ...flashSaleForm, target_value: e.target.value })}
                  placeholder={flashSaleForm.target_type === "collection" ? "e.g., sneaker" : "e.g., SH-101-Black-42"}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Discount Percentage *</label>
              <Input
                type="number"
                value={flashSaleForm.discount_percentage}
                onChange={(e) => setFlashSaleForm({ ...flashSaleForm, discount_percentage: parseFloat(e.target.value) })}
                min="5"
                max="70"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Start Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={flashSaleForm.start_date}
                  onChange={(e) => setFlashSaleForm({ ...flashSaleForm, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">End Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={flashSaleForm.end_date}
                  onChange={(e) => setFlashSaleForm({ ...flashSaleForm, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlashSaleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createFlashSale} className="bg-purple-600 hover:bg-purple-700">
              <Zap className="w-4 h-4 mr-2" />
              Create Flash Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Price Dialog */}
      <Dialog open={showComparePriceDialog} onOpenChange={setShowComparePriceDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Compare At Price (Show Discount)</DialogTitle>
            <DialogDescription>
              Add "original price" to show customers they're getting a deal
            </DialogDescription>
          </DialogHeader>
          
          {comparePricePreview && (
            <div className="py-4 space-y-4">
              {/* Logic Explanation */}
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">💡 How It Works</h4>
                <p className="text-sm text-purple-800 font-mono mb-2">{comparePricePreview.logic_explanation}</p>
                <p className="text-sm text-purple-700">
                  <strong>Example:</strong> {comparePricePreview.example}
                </p>
                <p className="text-xs text-purple-600 mt-2">
                  Customers will see the compare price crossed out, making your sale price look better!
                </p>
              </div>

              {/* Markup Slider */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Adjust Compare Price Markup: <span className="text-purple-600 text-lg font-bold">+{comparePriceMarkup}%</span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={comparePriceMarkup}
                  onChange={(e) => {
                    setComparePriceMarkup(parseInt(e.target.value));
                    previewComparePrice();
                  }}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>10% (Small discount)</span>
                  <span>50% (Medium)</span>
                  <span>100% (Large discount)</span>
                </div>
              </div>

              {/* Preview Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">Preview Changes (Showing {Math.min(20, comparePricePreview.preview_items.length)} of {comparePricePreview.total_items} items)</h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="select-all-compare"
                      checked={selectAllComparePrice}
                      onChange={(e) => handleSelectAllComparePrice(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="select-all-compare" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Select All ({comparePricePreview.total_items} items)
                    </label>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <span className="sr-only">Select</span>
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700">SKU</TableHead>
                      <TableHead className="font-semibold text-gray-700">Collection</TableHead>
                      <TableHead className="font-semibold text-gray-700">Sale Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">Current Compare</TableHead>
                      <TableHead className="font-semibold text-gray-700">New Compare Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">Discount Shown</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparePricePreview.preview_items.slice(0, 20).map((item) => (
                      <TableRow key={item.sku} className="hover:bg-gray-50">
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedComparePriceItems.includes(item.sku)}
                            onChange={() => handleToggleComparePriceItem(item.sku)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {item.collection}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          Rs. {item.sale_price.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {item.current_compare_at_price > 0 ? `Rs. ${item.current_compare_at_price.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="font-bold text-purple-600">
                          Rs. {item.new_compare_at_price.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                            {item.discount_shown}% OFF
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {comparePricePreview.total_items > 20 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    ... and {comparePricePreview.total_items - 20} more items
                  </p>
                )}
              </div>

              {/* Customer View Example */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">👥 What Customers Will See</h4>
                <div className="bg-white rounded p-3 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Product Name</p>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 line-through text-lg">
                      Rs. {comparePricePreview.preview_items[0]?.new_compare_at_price.toFixed(0)}
                    </span>
                    <span className="text-green-600 font-bold text-2xl">
                      Rs. {comparePricePreview.preview_items[0]?.sale_price.toFixed(0)}
                    </span>
                    <Badge variant="outline" className="bg-red-500 text-white border-red-500">
                      SAVE {comparePricePreview.preview_items[0]?.discount_shown}%
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComparePriceDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={applyComparePrice}
              disabled={applyingComparePrice}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {applyingComparePrice ? "Applying..." : selectedComparePriceItems.length > 0 
                ? `Apply to ${selectedComparePriceItems.length} Selected Items` 
                : `Apply to All ${comparePricePreview?.total_items || 0} Items`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Markup Results Dialog (After Applying) */}
      <Dialog open={showMarkupResultsDialog} onOpenChange={setShowMarkupResultsDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🎉</span>
              Pricing Applied Successfully!
            </DialogTitle>
            <DialogDescription>
              {markupResults?.updated_count || 0} items have been updated with new prices
            </DialogDescription>
          </DialogHeader>
          
          {markupResults && (
            <div className="py-4 space-y-4">
              {/* Success Banner */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-2xl">✓</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-900 text-lg">Markup Applied!</h4>
                    <p className="text-sm text-green-800 mt-1">
                      {markupResults.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Items Updated</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{markupResults.updated_count}</p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Old Total Value</p>
                    <p className="text-2xl font-bold text-gray-600 mt-1">
                      Rs. {(markupResults.summary.total_old_value / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">New Total Value</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      Rs. {(markupResults.summary.total_sale_value / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Total Profit</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      Rs. {(markupResults.summary.total_profit / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Applied Prices Table */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Updated Prices (Sample)</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700">SKU</TableHead>
                      <TableHead className="font-semibold text-gray-700">Collection</TableHead>
                      <TableHead className="font-semibold text-gray-700">Cost Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">❌ Old Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">✅ New Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">Profit</TableHead>
                      <TableHead className="font-semibold text-gray-700">Profit %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {markupResults.preview_items.slice(0, 15).map((item) => (
                      <TableRow key={item.sku} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {item.collection}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-900">
                          Rs. {item.cost.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-gray-400 line-through">
                          {item.old_price > 0 ? `Rs. ${item.old_price.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="font-bold text-green-600 text-base">
                          Rs. {item.new_price.toFixed(2)}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          Rs. {item.profit.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                            {item.profit_percentage}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {markupResults.updated_count > 15 && (
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    Showing 15 of {markupResults.updated_count} updated items
                  </p>
                )}
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">✨ Next Steps</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Prices are now live in your inventory</li>
                  <li>• Review slow-moving items for additional discounts</li>
                  <li>• Create flash sales to boost conversions</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowMarkupResultsDialog(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Markup Preview Dialog */}
      <Dialog open={showMarkupPreviewDialog} onOpenChange={setShowMarkupPreviewDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: 30% Markup Application</DialogTitle>
            <DialogDescription>
              Review changes before applying. {markupPreview?.total_items || 0} items will be updated
            </DialogDescription>
          </DialogHeader>
          
          {markupPreview && (
            <div className="py-4 space-y-4">
              {/* Logic Explanation */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📐 Pricing Logic</h4>
                <p className="text-sm text-blue-800 font-mono">{markupPreview.logic_explanation}</p>
                <p className="text-xs text-blue-700 mt-2">
                  Example: If Cost = Rs. 1,000 → New Price = Rs. 1,000 × 1.30 = Rs. 1,300 → Profit = Rs. 300 (30%)
                </p>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Items to Update</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{markupPreview.total_items}</p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Old Total Value</p>
                    <p className="text-2xl font-bold text-gray-600 mt-1">
                      Rs. {(markupPreview.summary.total_old_value / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">New Total Value</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      Rs. {(markupPreview.summary.total_new_value / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">Total Profit</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      Rs. {(markupPreview.summary.total_profit / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Items Table */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Preview Changes (First 50 items)</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700">SKU</TableHead>
                      <TableHead className="font-semibold text-gray-700">Collection</TableHead>
                      <TableHead className="font-semibold text-gray-700">Cost Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">Old Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">New Price</TableHead>
                      <TableHead className="font-semibold text-gray-700">Profit</TableHead>
                      <TableHead className="font-semibold text-gray-700">Profit %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {markupPreview.preview_items.slice(0, 20).map((item) => (
                      <TableRow key={item.sku} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {item.collection}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-900">
                          Rs. {item.cost.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {item.old_price > 0 ? `Rs. ${item.old_price.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          Rs. {item.new_price.toFixed(2)}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          Rs. {item.profit.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                            {item.profit_percentage}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {markupPreview.preview_items.length > 20 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    ... and {markupPreview.preview_items.length - 20} more items
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkupPreviewDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => applyMarkup(30)}
              disabled={applyingMarkup}
              className="bg-green-600 hover:bg-green-700"
            >
              {applyingMarkup ? "Applying..." : `Apply Markup to ${markupPreview?.total_items || 0} Items`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slow Moving Items Dialog */}
      <Dialog open={showSlowMovingDialog} onOpenChange={setShowSlowMovingDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Slow-Moving Inventory</DialogTitle>
            <DialogDescription>
              Items that haven't been ordered in 30+ days with suggested discounts
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {slowMovingItems.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No slow-moving items found!</p>
            ) : (
              <>
                {/* Historical Analysis Summary */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">📊 Historical Analysis</h4>
                  <p className="text-sm text-blue-800">
                    Analyzed <strong>1,039 inventory items</strong> against <strong>ALL 22,653 historical Shopify orders</strong> from day one.
                  </p>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="bg-white rounded p-2 border border-blue-200">
                      <p className="text-xs text-gray-600">Ever Ordered</p>
                      <p className="text-lg font-bold text-green-600">689 (66.3%)</p>
                    </div>
                    <div className="bg-white rounded p-2 border border-blue-200">
                      <p className="text-xs text-gray-600">Never Ordered</p>
                      <p className="text-lg font-bold text-red-600">350 (33.7%)</p>
                    </div>
                    <div className="bg-white rounded p-2 border border-blue-200">
                      <p className="text-xs text-gray-600">Total Inventory</p>
                      <p className="text-lg font-bold text-blue-600">1,039</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    ✓ Fuzzy matching: Exact SKU, case-insensitive, and base product + color patterns
                  </p>
                </div>
              </>
            )}
            {slowMovingItems.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-gray-700 w-12">
                      <input
                        type="checkbox"
                        checked={selectedSlowMovingItems.length === slowMovingItems.slice(0, 50).length && slowMovingItems.length > 0}
                        onChange={(e) => handleSelectAllSlowMoving(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">SKU</TableHead>
                    <TableHead className="font-semibold text-gray-700">Collection</TableHead>
                    <TableHead className="font-semibold text-gray-700">Cost</TableHead>
                    <TableHead className="font-semibold text-gray-700">Last Ordered</TableHead>
                    <TableHead className="font-semibold text-gray-700">Days Since Order</TableHead>
                    <TableHead className="font-semibold text-gray-700">Order Count</TableHead>
                    <TableHead className="font-semibold text-gray-700">Match Type</TableHead>
                    <TableHead className="font-semibold text-gray-700">Suggested Discount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slowMovingItems.slice(0, 50).map((item) => (
                    <TableRow key={item.sku} className="hover:bg-gray-50">
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedSlowMovingItems.some((si) => si.sku === item.sku)}
                          onChange={() => handleSelectSlowMovingItem(item.sku, item.suggested_discount)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {item.collection}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">Rs. {item.cost.toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {item.last_ordered || "Never"}
                      </TableCell>
                      <TableCell>
                        {item.days_since_last_order !== null ? (
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                            {item.days_since_last_order} days
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                            Never
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {item.order_count || 0}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {item.match_type || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                          -{item.suggested_discount}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {slowMovingItems.length > 0 && (
              <div className="mt-4 space-y-3">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Total Value:</strong> Rs. {slowMovingItems.reduce((sum, item) => sum + item.cost, 0).toFixed(2)} 
                    <span className="ml-4">•</span>
                    <strong className="ml-4">{slowMovingItems.length} items</strong>
                  </p>
                </div>
                
                {selectedSlowMovingItems.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-green-900">
                        {selectedSlowMovingItems.length} items selected
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Suggested discounts will be applied to help liquidate inventory
                      </p>
                    </div>
                    <Button
                      onClick={applyDiscountsToSlowMoving}
                      disabled={applyingDiscounts}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {applyingDiscounts ? "Applying..." : `Apply Discounts to ${selectedSlowMovingItems.length} Items`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Shopify Products Dialog */}
      <Dialog open={showShopifyProductsDialog} onOpenChange={setShowShopifyProductsDialog}>
        <DialogContent className="max-w-7xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Shopify Products</DialogTitle>
            <DialogDescription>
              View all products synced from your Shopify store, grouped by collection
            </DialogDescription>
          </DialogHeader>
          
          {shopifyProducts && (
            <div className="py-4 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {shopifyProducts.total_products}
                      </p>
                      <p className="text-sm text-blue-600">Total Products</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-700">
                        {Object.keys(shopifyProducts.collections).length}
                      </p>
                      <p className="text-sm text-purple-600">Collections</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {shopifyProducts.products.reduce((sum, p) => sum + p.variants.length, 0)}
                      </p>
                      <p className="text-sm text-green-600">Total Variants</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Collection Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Filter by Collection
                </label>
                <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Collections</SelectItem>
                    {Object.entries(shopifyProducts.collections).map(([name, count]) => (
                      <SelectItem key={name} value={name}>
                        {name} ({count} products)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Products grouped by collection */}
              {Object.entries(shopifyProducts.grouped_by_collection)
                .filter(([collectionName]) => 
                  selectedCollection === "all" || selectedCollection === collectionName
                )
                .map(([collectionName, products]) => (
                  <div key={collectionName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {collectionName}
                      </h3>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700">
                        {products.length} products
                      </Badge>
                    </div>
                    
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold text-gray-700">Product</TableHead>
                          <TableHead className="font-semibold text-gray-700">Vendor</TableHead>
                          <TableHead className="font-semibold text-gray-700">Status</TableHead>
                          <TableHead className="font-semibold text-gray-700">Variants</TableHead>
                          <TableHead className="font-semibold text-gray-700">Price Range</TableHead>
                          <TableHead className="font-semibold text-gray-700">Total Stock</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((product) => {
                          const prices = product.variants.map(v => v.price);
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);
                          const totalStock = product.variants.reduce((sum, v) => sum + v.inventory_quantity, 0);
                          
                          return (
                            <TableRow key={product.id} className="hover:bg-gray-50">
                              <TableCell>
                                <div>
                                  <p className="font-medium text-gray-900">{product.title}</p>
                                  {product.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {product.tags.slice(0, 3).map((tag, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs bg-gray-50">
                                          {tag.trim()}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                  {product.vendor || "N/A"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant="outline" 
                                  className={
                                    product.status === "active" 
                                      ? "bg-green-100 text-green-700 border-green-300" 
                                      : "bg-gray-100 text-gray-700 border-gray-300"
                                  }
                                >
                                  {product.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-purple-50 text-purple-700">
                                  {product.variants.length}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-semibold text-green-600">
                                {minPrice === maxPrice 
                                  ? `Rs. ${minPrice.toFixed(2)}`
                                  : `Rs. ${minPrice.toFixed(2)} - Rs. ${maxPrice.toFixed(2)}`
                                }
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge 
                                  variant="outline" 
                                  className={
                                    totalStock > 0 
                                      ? "bg-green-100 text-green-700" 
                                      : "bg-red-100 text-red-700"
                                  }
                                >
                                  {totalStock}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ))}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowShopifyProductsDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingDashboard;
